export const AI_CHAT_TEXT_MODELS = [
  { id: 'gpt-5.6-sol', name: 'GPT-5.6 Sol', organization: 'OpenAI', openRouterId: 'openai/gpt-5.6-sol', promptUsdPerMillion: 2, completionUsdPerMillion: 10, free: false },
  { id: 'gpt-5.4', name: 'GPT-5.4', organization: 'OpenAI', openRouterId: 'openai/gpt-5.4', promptUsdPerMillion: 2.5, completionUsdPerMillion: 15, free: false },
  { id: 'gpt-5.6-terra', name: 'GPT-5.6 Terra', organization: 'OpenAI', openRouterId: 'openai/gpt-5.6-terra', promptUsdPerMillion: 2, completionUsdPerMillion: 12, free: false },
  { id: 'gpt-5.5', name: 'GPT-5.5', organization: 'OpenAI', openRouterId: 'openai/gpt-5.5', promptUsdPerMillion: 5, completionUsdPerMillion: 30, free: false },
  { id: 'gemini-3-flash', name: 'Gemini 3 Flash Preview', organization: 'Google', openRouterId: 'google/gemini-3-flash-preview', promptUsdPerMillion: 0.5, completionUsdPerMillion: 3, free: true },
  { id: 'gemini-3.1-pro', name: 'Gemini 3.1 Pro Preview', organization: 'Google', openRouterId: 'google/gemini-3.1-pro-preview', promptUsdPerMillion: 2, completionUsdPerMillion: 12, free: false },
  { id: 'gemini-3.1-flash-lite', name: 'Gemini 3.1 Flash-Lite', organization: 'Google', openRouterId: 'google/gemini-3.1-flash-lite', promptUsdPerMillion: 0.25, completionUsdPerMillion: 1.5, free: true },
  { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash', organization: 'Google', openRouterId: 'google/gemini-3.5-flash', promptUsdPerMillion: 1.5, completionUsdPerMillion: 9, free: false },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', organization: 'Google', openRouterId: 'google/gemini-2.5-flash', promptUsdPerMillion: 0.3, completionUsdPerMillion: 2.5, free: true },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', organization: 'Google', openRouterId: 'google/gemini-2.5-pro', promptUsdPerMillion: 1.25, completionUsdPerMillion: 10, free: false },
  { id: 'claude-sonnet-5', name: 'Claude Sonnet 5', organization: 'Anthropic', openRouterId: 'anthropic/claude-sonnet-5', promptUsdPerMillion: 2, completionUsdPerMillion: 10, free: false },
  { id: 'claude-opus-5', name: 'Claude Opus 5', organization: 'Anthropic', openRouterId: 'anthropic/claude-opus-5', promptUsdPerMillion: 5, completionUsdPerMillion: 25, free: false },
  { id: 'deepseek-v4-flash-0731', name: 'DeepSeek V4 Flash 0731', organization: 'DeepSeek', openRouterId: 'deepseek/deepseek-v4-flash-0731', promptUsdPerMillion: 0.065, completionUsdPerMillion: 0.18, free: false },
  { id: 'qwen3.8-flash', name: 'Qwen3.8 Flash', organization: 'Qwen', openRouterId: 'qwen/qwen3.8-flash', promptUsdPerMillion: 0.15, completionUsdPerMillion: 0.47, free: false },
  { id: 'kimi-k3', name: 'Kimi K3', organization: 'MoonshotAI', openRouterId: 'moonshotai/kimi-k3', promptUsdPerMillion: 3, completionUsdPerMillion: 15, free: false },
  { id: 'grok-4.6', name: 'Grok 4.6', organization: 'SpaceXAI', openRouterId: 'x-ai/grok-4.6', promptUsdPerMillion: 2, completionUsdPerMillion: 6, free: false },
] as const;

export type AiChatModelId = (typeof AI_CHAT_TEXT_MODELS)[number]['id'];
export type AiChatModel = (typeof AI_CHAT_TEXT_MODELS)[number];
export type AiChatModelPlan = 'standard' | 'plus' | 'pro';

export const DEFAULT_AI_CHAT_MODEL_ID: AiChatModelId = 'gpt-5.6-terra';
export const DEFAULT_FREE_AI_CHAT_MODEL_ID: AiChatModelId = 'gemini-3.1-flash-lite';

const modelIds = new Set<string>(AI_CHAT_TEXT_MODELS.map((model) => model.id));

export function isAiChatModelId(value: unknown): value is AiChatModelId {
  return typeof value === 'string' && modelIds.has(value);
}

export function getAiChatModel(value: unknown): AiChatModel | undefined {
  if (!isAiChatModelId(value)) return undefined;
  return AI_CHAT_TEXT_MODELS.find((model) => model.id === value);
}

export function getAiChatModelsForPlan(plan: AiChatModelPlan): readonly AiChatModel[] {
  return plan === 'standard' ? AI_CHAT_TEXT_MODELS.filter((model) => model.free) : AI_CHAT_TEXT_MODELS;
}

export function groupAiChatModelsByOrganization(models: readonly AiChatModel[]) {
  const groups = new Map<AiChatModel['organization'], AiChatModel[]>();
  for (const model of models) {
    const group = groups.get(model.organization);
    if (group) group.push(model);
    else groups.set(model.organization, [model]);
  }
  return Array.from(groups, ([organization, models]) => ({ organization, models }));
}

export function isAiChatModelAvailableForPlan(model: AiChatModel, plan: AiChatModelPlan): boolean {
  return plan !== 'standard' || model.free;
}

export function resolveAiChatDefaultModelId(value: unknown): AiChatModelId {
  return isAiChatModelId(value) ? value : DEFAULT_AI_CHAT_MODEL_ID;
}

export function getAiChatDefaultModelIdForPlan(plan: AiChatModelPlan, configured?: unknown): AiChatModelId {
  if (plan === 'standard') return DEFAULT_FREE_AI_CHAT_MODEL_ID;
  return resolveAiChatDefaultModelId(configured);
}
